/**
 * Local/synthetic market snapshot (dashboard hero).
 *
 * Deterministic last/prev closes from `seedMarketSnapshot` (`code + 'm' + 'v3'`).
 * asOf is null: local mode has no ingest (same honesty as `buildMeta`).
 */
import {
  SNAPSHOT_INSTRUMENTS,
  type MarketSnapshotItem,
  type MarketSnapshotPayload,
  type SnapshotInstrument,
} from '@meridian/contracts';

import { snapshotItemFromCloses } from '../../analytics/market-snapshot';
import { fnv1a } from '../../rand/fnv1a';
import { createRng } from '../../rand/mulberry32';
import { seedMarketSnapshot } from '../../rand/seed';

/** Frozen as-of for the synthetic last bar — not "today". */
const SYNTHETIC_AS_ON = '2026-08-25';
/** The bar before it. Named so the pair cannot silently drift apart. */
const SYNTHETIC_PREV_ON = '2026-08-24';

/** Jitter widths around `LAST_BASE`; fractions, applied symmetrically. */
const LAST_JITTER = 0.02;
const DAY_RETURN_SPREAD = 0.04;

/**
 * Plausible level bases so local mode reads as a market tape, not a unit
 * interval. Draws jitter around these; they are not live quotes.
 */
const LAST_BASE: Readonly<Record<SnapshotInstrument['code'], number>> = {
  SPX: 7653,
  DJI: 53417,
  COMP: 25980,
  RUT: 2995,
  VIX: 15.85,
  BTC: 80827,
  ETH: 2517,
  GOLD: 4711,
  CL: 85.77,
  US10Y: 4.7,
};

function synthesizeItem(instrument: SnapshotInstrument): MarketSnapshotItem {
  const rng = createRng(fnv1a(seedMarketSnapshot(instrument.code)));
  const last = LAST_BASE[instrument.code] * (1 + (rng() - 0.5) * LAST_JITTER);
  const ret = (rng() - 0.5) * DAY_RETURN_SPREAD;
  const prev = last / (1 + ret);
  const item = snapshotItemFromCloses(
    instrument,
    { on: SYNTHETIC_AS_ON, close: last },
    { on: SYNTHETIC_PREV_ON, close: prev },
  );
  if (item === null) {
    throw new Error(`synthetic snapshot produced no item for ${instrument.code}`);
  }
  return item;
}

export function buildMarketSnapshot(): MarketSnapshotPayload {
  return {
    asOf: null,
    items: SNAPSHOT_INSTRUMENTS.map(synthesizeItem),
  };
}
